/**
 * OS NÚMEROS QUE O SERVIÇO ESTÁ USANDO AGORA — T17.3.
 *
 * ── O DEFEITO QUE ESTE ARQUIVO EVITA ────────────────────────────────────────────────────────
 *
 * As regras saíram do código e foram para o banco (T17). O serviço passou a lê-las de lá; **a tela
 * não**. Ela continuava com 90 e 45 escritos, e no dia em que alguém mudasse o número no painel a
 * dica diria 90 enquanto o desenho usaria 60.
 *
 * É exatamente o defeito do T16.3, que custou uma rodada de QA: *"a legenda mostra o texto inteiro
 * e o desenho tem 90, e nada na tela dizia isso"*. Trocar um número por outro não conserta nada se
 * a tela continua afirmando o antigo.
 *
 * ── POR QUE AINDA HÁ UM NÚMERO ESCRITO AQUI ─────────────────────────────────────────────────
 *
 * `PADRAO` é o que a tela mostra ENQUANTO a resposta do serviço não chegou, e quando ela não chega.
 * Sem ele, a dica abriria em branco ou com "…", e uma dica que demora é uma dica que ninguém lê.
 *
 * Ele não decide nada: quem corta a frase é o serviço, e esta é a legenda daquele corte. Um número
 * de legenda defasado por alguns segundos é diferente de uma regra defasada — a primeira some
 * sozinha, a segunda vira desenho na página de uma criança.
 */

export interface NumerosDoDesenho {
  /** Quantas letras da cena entram no pedido quando ninguém descreveu o personagem. */
  maxCena: number;
  /** E quantas entram quando descreveu — o resto fica guardado para a descrição. */
  maxCenaComPersonagem: number;
}

/** O que a tela mostra enquanto o serviço não respondeu. Mesmos valores que ele traz hoje. */
export const PADRAO: NumerosDoDesenho = { maxCena: 90, maxCenaComPersonagem: 45 };

/**
 * A conta que o serviço faz, refeita aqui a partir dos números dele.
 *
 * `maxCenaComPersonagem` não existe no conjunto de regras: lá moram as PEÇAS — o teto do pedido, a
 * reserva do personagem, o piso da cena — e o número que a criança vê sai delas. Refazer a conta
 * aqui é o preço de a tela falar em letras de cena, que é o que a pessoa entende, em vez de falar
 * em orçamento de pedido, que ela não tem por que entender.
 *
 * `caudaDoPedido` é o tamanho do texto de estética e segurança que a plataforma cola em todo
 * pedido. Ele não está no conjunto de regras porque não é regra editável — e por isso entra aqui
 * como a medição que ele é: 203 letras, medidas em 03/10/2026.
 */
export const CAUDA_DO_PEDIDO = 203;

export function numerosDe(desenho: {
  tetoDoPositivo: number;
  maxCena: number;
  minCena: number;
  reservaDoPersonagem: number;
}): NumerosDoDesenho {
  const doTextoDaCrianca = desenho.tetoDoPositivo - CAUDA_DO_PEDIDO - 2;
  const guardado = Math.max(0, Math.min(desenho.reservaDoPersonagem, doTextoDaCrianca - desenho.minCena));
  return {
    maxCena: Math.max(desenho.minCena, Math.min(desenho.maxCena, doTextoDaCrianca)),
    maxCenaComPersonagem: Math.max(desenho.minCena, Math.min(desenho.maxCena, doTextoDaCrianca - guardado)),
  };
}

/**
 * Pergunta ao serviço quais números estão valendo.
 *
 * Nunca lança e nunca demora: qualquer problema devolve o padrão. Uma tela de criança que não abre
 * porque a rota de diagnóstico não respondeu seria um defeito muito maior do que uma dica com o
 * número de ontem.
 */
export async function perguntarAoServico(): Promise<NumerosDoDesenho> {
  try {
    const r = await fetch(`${process.env.REACT_APP_BACKEND_ROUTE}/regras`, {
      signal: AbortSignal.timeout(4000),
    });
    if (!r.ok) return PADRAO;
    const corpo = await r.json();
    const d = corpo?.regras?.desenho;
    if (!d || typeof d.tetoDoPositivo !== "number") return PADRAO;
    return numerosDe(d);
  } catch {
    return PADRAO;
  }
}
